import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { news as fallbackNews } from "@/lib/dummyData";
import { NewsAPI } from "@/lib/api";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

export default function News() {
  // Awali dengan data dummy; ganti begitu backend merespons.
  const [items, setItems] = useState(fallbackNews.map((n) => ({ ...n, date: n.date })));

  useEffect(() => {
    let alive = true;
    NewsAPI.list()
      .then((rows) => {
        if (alive && rows?.length) setItems(rows.map((a) => ({ ...a, date: a.published_at })));
      })
      .catch(() => { /* pakai fallback dummy */ });
    return () => { alive = false; };
  }, []);

  if (!items.length) return null;
  const featured = items[0];
  const rest = items.slice(1);
  const linkTo = (n) => `/berita/${n.slug || n.id}`;

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-16">
      <div className="pill-tag bg-terracotta/10 text-terracotta mb-4">Berita & Pengumuman</div>
      <h1 className="font-display text-4xl lg:text-6xl font-bold text-ink tracking-tight">Kabar dari koperasi</h1>
      <p className="text-ink/75 mt-3 max-w-xl">Update terbaru kegiatan, program, dan pengumuman penting untuk seluruh anggota dan masyarakat.</p>

      {/* Featured */}
      <article className="mt-12 grid lg:grid-cols-12 gap-8 items-center">
        <Link to={linkTo(featured)} className="lg:col-span-7 rounded-3xl overflow-hidden border border-stone-soft aspect-[16/10] block group">
          <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        </Link>
        <div className="lg:col-span-5">
          <Badge className="bg-amber-warm text-ink hover:bg-amber-warm border-0 rounded-full text-[10px] uppercase tracking-wider">{featured.category}</Badge>
          <Link to={linkTo(featured)}>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-ink mt-3 leading-tight hover:text-forest transition">{featured.title}</h2>
          </Link>
          <p className="text-ink/75 mt-4 leading-relaxed">{featured.excerpt}</p>
          <div className="flex items-center gap-2 mt-5 text-sm text-ink/55">
            <Calendar size={14} /> {fmtDate(featured.date)}
          </div>
          <Link to={linkTo(featured)} className="mt-6 inline-flex items-center gap-1 text-forest font-semibold hover:gap-2 transition-all">
            Baca artikel lengkap <ArrowUpRight size={16} />
          </Link>
        </div>
      </article>

      <div className="mt-16 leaf-divider h-4 opacity-60"></div>

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((n) => (
          <Link key={n.id} to={linkTo(n)} className="group rounded-2xl border border-stone-soft bg-white overflow-hidden hover-lift block" data-testid={`news-item-${n.id}`}>
            <div className="aspect-[16/10] overflow-hidden bg-cream">
              <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <Badge className="bg-sage/20 text-forest hover:bg-sage/30 border-0 rounded-full text-[10px] uppercase tracking-wider">{n.category}</Badge>
              <h3 className="font-display font-semibold text-ink mt-3 text-lg leading-snug line-clamp-2 group-hover:text-forest transition">{n.title}</h3>
              <p className="text-sm text-ink/75 mt-2 line-clamp-2">{n.excerpt}</p>
              <div className="text-xs text-ink/55 mt-4">{fmtDate(n.date)}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
